/**
 * Push live sync state to the Collect&Go tab overlay (extension HUD).
 */

import { cogoCommand } from './cogoExtension'
import type { CogoSyncSnapshot } from './cogoSyncWorker'

const SYNC_END_HUD_MS = 3_500

export type SyncHudPayload = {
  visible?: boolean
  orderId: number
  status: string
  phase: string
  itemIndex: number
  itemCount: number
  currentDisplay?: string
  pendingCmd?: {
    type: string
    quantity?: number
    quantityUnit?: 'st' | 'kg'
    unitType?: string | null
    title?: string
    assortmentUrl?: string
    url?: string
  } | null
  log?: Array<{ message: string }>
  /** Highlight end-of-sync summary in the overlay. */
  summary?: string[]
}

export function snapshotToHudPayload(
  snap: CogoSyncSnapshot,
  pendingCmd?: SyncHudPayload['pendingCmd']
): SyncHudPayload {
  const summary = extractSyncSummary(snap)
  const terminal =
    snap.status === 'done' ||
    snap.status === 'error' ||
    snap.status === 'waiting_choice'
  return {
    visible: true,
    orderId: snap.orderId,
    status: snap.status,
    phase: snap.phase,
    itemIndex: snap.itemIndex,
    itemCount: snap.itemCount,
    currentDisplay: terminal ? undefined : snap.currentDisplay,
    pendingCmd: terminal
      ? null
      : (pendingCmd ??
        (snap.pendingCmdType ? { type: snap.pendingCmdType } : null)),
    log: (terminal ? snap.log.slice(-12) : snap.log.slice(-4)).map((l) => ({
      message: l.message,
    })),
    summary: summary.length > 0 ? summary : undefined,
  }
}

function extractSyncSummary(snap: CogoSyncSnapshot): string[] {
  const lines = snap.log
    .filter((l) =>
      /gesynchroniseerd|Mislukt\s*\(|Keuze nodig|Overgeslagen|Sync geannuleerd|Sync klaar/i.test(
        l.message
      )
    )
    .map((l) => l.message)
  if (lines.length > 0) return lines

  if (snap.status !== 'done' && snap.status !== 'error') return []

  const ok = snap.items.filter((i) => i.status === 'ok').length
  const failed = snap.items.filter(
    (i) => i.status === 'error' || i.status === 'not_found'
  )
  const out = [`Sync klaar — ${ok}/${snap.items.length} gesynchroniseerd`]
  if (failed.length > 0) {
    out.push(
      `Mislukt (${failed.length}): ${failed.map((i) => i.display).join('; ')}`
    )
  }
  return out
}

export async function pushSyncHud(payload: SyncHudPayload): Promise<void> {
  try {
    await cogoCommand({ type: 'updateSyncHud', payload }, 8_000)
  } catch {
    /* overlay is best-effort */
  }
}

export async function hideSyncHud(): Promise<void> {
  try {
    await cogoCommand({ type: 'hideSyncHud' }, 5_000)
  } catch {
    /* ignore */
  }
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms))
}

/** Show end-of-sync summary on the HUD, then hide after a short delay. */
export async function showSyncEndHudThenHide(
  snap: CogoSyncSnapshot
): Promise<void> {
  await pushSyncHud(snapshotToHudPayload(snap))
  await sleep(SYNC_END_HUD_MS)
  await hideSyncHud()
}

export async function pushSyncSnapshot(
  snap: CogoSyncSnapshot,
  pendingCmd?: SyncHudPayload['pendingCmd']
): Promise<void> {
  if (snap.status === 'done' || snap.status === 'error') {
    // Keep HUD up with the summary; caller delays hide via showSyncEndHudThenHide.
    await pushSyncHud(snapshotToHudPayload(snap, pendingCmd))
    return
  }
  if (snap.status === 'paused' && snap.pendingCmdType == null) {
    await pushSyncHud(snapshotToHudPayload(snap, pendingCmd))
    return
  }
  await pushSyncHud(snapshotToHudPayload(snap, pendingCmd))
}
